import { useEffect, useRef, useState } from 'react';

import type { Identifiable, UseGridRowSelectionOptions, UseGridRowSelectionReturn } from './types';

const useGridRowSelection = <T extends Identifiable>(
    rows: T[],
    options?: UseGridRowSelectionOptions<T>
): UseGridRowSelectionReturn => {
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const selectedGroupKeys = useRef<Set<string>>(new Set());
    const previousRowIds = useRef<Set<string>>(new Set());
    const getGroupKey = options?.getGroupKey;

    const allIds = new Set(rows.map(row => row.id));

    useEffect(() => {
        if (!getGroupKey || selectedGroupKeys.current.size === 0) {
            return;
        }

        const newRows = rows.filter(row => !previousRowIds.current.has(row.id));

        if (newRows.length === 0) {
            return;
        }

        const idsToAdd = newRows
            .filter(row => selectedGroupKeys.current.has(getGroupKey(row)))
            .map(row => row.id);

        if (idsToAdd.length > 0) {
            setSelectedIds(previous => {
                const next = new Set(previous);

                idsToAdd.forEach(id => next.add(id));

                return next;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);

    useEffect(() => {
        previousRowIds.current = new Set(rows.map(row => row.id));
    }, [rows]);

    const isRowSelected = (id: string) => selectedIds.has(id);

    const isGroupSelected = (items: Identifiable[]) =>
        items.length > 0 && items.every(item => selectedIds.has(item.id));

    const isGroupIndeterminate = (items: Identifiable[]) => {
        const selectedCount = items.filter(item => selectedIds.has(item.id)).length;

        return selectedCount > 0 && selectedCount < items.length;
    };

    const isAllSelected =
        allIds.size > 0 &&
        allIds.size === selectedIds.size &&
        [...allIds].every(id => selectedIds.has(id));

    const isAllIndeterminate = !isAllSelected && selectedIds.size > 0;

    const toggleRow = (id: string) => {
        setSelectedIds(previous => {
            const next = new Set(previous);

            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }

            return next;
        });

        if (getGroupKey) {
            const row = rows.find(r => r.id === id);

            if (row) {
                selectedGroupKeys.current.delete(getGroupKey(row));
            }
        }
    };

    const toggleGroup = (items: Identifiable[]) => {
        setSelectedIds(previous => {
            const next = new Set(previous);
            const allSelected = items.every(item => next.has(item.id));

            if (allSelected) {
                items.forEach(item => next.delete(item.id));
            } else {
                items.forEach(item => next.add(item.id));
            }

            if (getGroupKey && items.length > 0) {
                const groupKey = getGroupKey(items[0] as T);

                if (allSelected) {
                    selectedGroupKeys.current.delete(groupKey);
                } else {
                    selectedGroupKeys.current.add(groupKey);
                }
            }

            return next;
        });
    };

    const toggleAll = () => {
        if (isAllSelected) {
            setSelectedIds(new Set());
            selectedGroupKeys.current.clear();
        } else {
            setSelectedIds(new Set(allIds));

            if (getGroupKey) {
                selectedGroupKeys.current = new Set(rows.map(row => getGroupKey(row)));
            }
        }
    };

    return {
        isAllIndeterminate,
        isAllSelected,
        isGroupIndeterminate,
        isGroupSelected,
        isRowSelected,
        selectedIds,
        toggleAll,
        toggleGroup,
        toggleRow
    };
};

export default useGridRowSelection;
